import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import {
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Tracking } from "~/instrumentation/tracking";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute("/onboarding/survey")({
  component: RouteComponent,
});

type FormValues = {
  companySize: string;
  position: string;
  useCase: string;
};

const formSchema = z.object({
  companySize: z.string().min(1, { message: "Company size is required" }),
  position: z.string().min(1, { message: "Position is required" }),
  useCase: z.string().min(1, { message: "Use case is required" }),
});

function RouteComponent() {
  const trpc = useTRPC();
  const completeOnboarding = useMutation(
    trpc.onboarding.completeSurveyStep.mutationOptions(),
  );

  const { onboardingState } = Route.useRouteContext();

  const form = useForm<FormValues>({
    defaultValues: {
      companySize: "",
      position: "",
      useCase: "",
    },
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormValues) => {
    await completeOnboarding.mutateAsync(data, {
      onSuccess: async () => {
        await onboardingState.next();
        toast.success("Welcome to newmode.ai!", {
          id: "welcome",
          description: "Create a variant of your website to get started",
          duration: 10_000,
          cancel: (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => toast.dismiss("welcome")}
            >
              <X />
            </Button>
          ),
        });
        Tracking.capture({ event: "ONBOARDING_COMPLETED" });
      },
    });
  };

  return (
    <>
      <CardHeader>
        <CardTitle>Tell us about yourself</CardTitle>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent>
            <FormField
              control={form.control}
              name="companySize"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Company Size</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select company size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1-10 employees</SelectItem>
                        <SelectItem value="11-50">11-50 employees</SelectItem>
                        <SelectItem value="51-200">51-200 employees</SelectItem>
                        <SelectItem value="201-500">
                          201-500 employees
                        </SelectItem>
                        <SelectItem value="501+">501+ employees</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="position"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Position</FormLabel>
                  <FormControl>
                    <Input placeholder="Your position" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="useCase"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>How will you use our product?</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Tell us about your use case"
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button type="submit" disabled={completeOnboarding.isPending}>
              {completeOnboarding.isPending && (
                <Loader2 className="animate-spin" />
              )}
              Continue
            </Button>
          </CardFooter>
        </form>
      </Form>
    </>
  );
}
